import { useMemo, useRef, useState, useEffect } from "react"
import Box from "@mui/material/Box"
import Button from "@mui/material/Button"
import Typography from "@mui/material/Typography"
import TextField from "@mui/material/TextField"
import Dialog from "@mui/material/Dialog"
import IconButton from "@mui/material/IconButton"
import { useMediaQuery, useTheme } from "@mui/material"
import { ThemeProvider, createTheme } from "@mui/material/styles"
import { COLORS, FONT_FAMILY, FONT_HEADING, BUTTON_HOVER_SX } from "../talkTreeShared/Tree/constants/theme"
import '../talkTreeShared/Tree/Tree.css'

import SimulatedTalkTreePreview, { SIM_MAX_FLOWERS as MAX_FLOWERS } from "../talkTreeShared/Tree/SimulatedTalkTreePreview"
import { saveSimulatedTree, getSimulatedTrees } from "../talkTreeShared/Tree/utils/simulatedTreeStorage"
import SimulatorHelpDialog from "./popups/Simulatorhelpdialog"

// When SET_SIMULATOR_TO_LAST_TREE is true, opening the simulator pre-fills answers from
// the user's last saved simulated tree. When false, the simulator always starts anew.
const SET_SIMULATOR_TO_LAST_TREE = false

const LEAF_COUNTS = [1, 15, 27, 40, 54]

const FLOWER_COUNTS = {
    making_claim: [0, 1, 3, 5, 7],
    providing_evidence: [0, 1, 3, 4, 6],
    relating: [0, 1, 3, 4, 6],
}


const TALK_MOVE_KEYS = ["making_claim", "providing_evidence", "relating"]

const TALK_MOVE_QUESTIONS = [
    {
        key: "making_claim",
        label: 'During a typical lesson, how often are your students giving short answers? (e.g. "I got x")',
    },
    {
        key: "providing_evidence",
        label: 'During a typical lesson, how often are they explaining their thoughts? (e.g. "I did it this way because…")',
    },
    {
        key: "relating",
        label: 'During a typical lesson, how often are they building on what another student has said? (e.g. "I got the same answer as him")',
    },
]

const ALL_QUESTIONS = [
    {
        key: "overallTalk",
        label: "During a typical lesson, how much do your students talk overall? (e.g. any talk at all, even if off topic)",
        lowLabel: "Very little",
        highLabel: "A lot",
    },
    ...TALK_MOVE_QUESTIONS.map((q) => ({
        ...q,
        lowLabel: "Rarely",
        highLabel: "Often",
    })),
]

const INITIAL_ANSWERS = {
    overallTalk: null,
    making_claim: null,
    providing_evidence: null,
    relating: null,
}



function computeFlowerCounts(answers) {
    const counts = {}
    for (const q of TALK_MOVE_QUESTIONS) {
        const ans = answers[q.key]
        counts[q.key] = ans == null ? 0 : FLOWER_COUNTS[q.key][ans - 1]
    }
    return counts
}

function buildInitialSlotMap(counts) {
    const pool = []
    for (const key of TALK_MOVE_KEYS) for (let i = 0; i < counts[key]; i++) pool.push(key)
    const indices = Array.from({ length: MAX_FLOWERS }, (_, i) => i)
    for (let i = indices.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1))
        ;[indices[i], indices[j]] = [indices[j], indices[i]]
    }
    const slotMap = new Array(MAX_FLOWERS).fill(null)
    for (let i = 0; i < pool.length; i++) slotMap[indices[i]] = pool[i]
    return slotMap
}

function updateSlotMapForType(prevSlotMap, type, newCount) {
    const slotMap = [...prevSlotMap]
    const currentIndices = []
    for (let i = 0; i < slotMap.length; i++) if (slotMap[i] === type) currentIndices.push(i)
    const currentCount = currentIndices.length
    if (newCount > currentCount) {
        const toAdd = newCount - currentCount
        const emptyIndices = []
        for (let i = 0; i < slotMap.length; i++) if (slotMap[i] === null) emptyIndices.push(i)
        for (let i = emptyIndices.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1))
            ;[emptyIndices[i], emptyIndices[j]] = [emptyIndices[j], emptyIndices[i]]
        }
        for (let i = 0; i < toAdd && i < emptyIndices.length; i++) slotMap[emptyIndices[i]] = type
    } else if (newCount < currentCount) {
        const toRemove = currentCount - newCount
        const shuffled = [...currentIndices]
        for (let i = shuffled.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1))
            ;[shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]]
        }
        for (let i = 0; i < toRemove; i++) slotMap[shuffled[i]] = null
    }
    return slotMap
}

function isFlowerSlotVisible(flowerId, slotMap) {
    const idx = parseInt(flowerId.replace("f", "")) - 1
    return idx < slotMap.length && slotMap[idx] !== null
}

function applySlotMapVisibility(branches, slotMap) {
    return branches.map((branch) => {
        const patched = { ...branch }
        patched.flowerVisible = isFlowerSlotVisible(patched.flowerId, slotMap)
        if (patched.innerBranches) {
            patched.innerBranches = patched.innerBranches.map((inner) => {
                const patchedInner = { ...inner }
                patchedInner.flowerVisible = isFlowerSlotVisible(patchedInner.flowerId, slotMap)
                if (patchedInner.subBranch) {
                    patchedInner.subBranch = { ...patchedInner.subBranch }
                    patchedInner.subBranch.flowerVisible = isFlowerSlotVisible(patchedInner.subBranch.flowerId, slotMap)
                }
                return patchedInner
            })
        }
        return patched
    })
}



function SliderSelector({ value, onChange, labelId }) {
    const sliderRef = useRef(null)
    const [displayValue, setDisplayValue] = useState(value ?? 1)
    const fillPercent = ((displayValue - 1) / 4) * 100
    useEffect(() => {
        if (value !== null) setDisplayValue(value)
    }, [value])
    const handleChange = (e) => {
        const val = parseInt(e.target.value)
        setDisplayValue(val)
        onChange(val)
    }
    return (
        <div style={{ padding: "0 10px", width: "100%", boxSizing: "border-box" }}>
            <div style={{ position: "relative", height: "28px", display: "flex", alignItems: "center" }}>
                <div style={{ position: "absolute", left: 0, right: 0, height: "6px", borderRadius: "3px", background: "#e5e7eb" }} />
                <div style={{ position: "absolute", left: 0, width: `${fillPercent}%`, height: "6px", borderRadius: "3px", background: COLORS.primary, transition: "width 0.1s ease" }} />
                {[1, 2, 3, 4, 5].map((v) => (
                    <div
                        key={v}
                        style={{
                            position: "absolute",
                            left: `${((v - 1) / 4) * 100}%`,
                            transform: "translate(-50%, -50%)",
                            top: "50%",
                            width: "10px",
                            height: "10px",
                            borderRadius: "50%",
                            background: v <= displayValue ? "rgba(255,255,255,0.8)" : "#e5e7eb",
                            zIndex: 1,
                            pointerEvents: "none",
                        }}
                    />
                ))}
                <input
                    ref={sliderRef}
                    type="range"
                    min={1}
                    max={5}
                    step={1}
                    value={displayValue}
                    onChange={handleChange}
                    aria-labelledby={labelId}
                    style={{ position: "absolute", width: "100%", height: "44px", margin: 0, opacity: 0, cursor: "pointer", zIndex: 3 }}
                />
                <div
                    style={{
                        position: "absolute",
                        left: `${fillPercent}%`,
                        transform: "translate(-50%, -50%)",
                        top: "50%",
                        width: "22px",
                        height: "22px",
                        borderRadius: "50%",
                        background: COLORS.primary,
                        border: "3px solid #fff",
                        boxShadow: "0 1px 4px rgba(0,0,0,0.2)",
                        zIndex: 2,
                        pointerEvents: "none",
                        transition: "left 0.1s ease",
                    }}
                />
            </div>
        </div>
    )
}

function QuestionBlock({ question, value, onChange, showHighlight, lowLabel, highLabel }) {
    const unanswered = value === null
    const labelId = `slider-label-${question.key}`
    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                gap: "6px",
                borderLeft: unanswered && showHighlight ? "3px solid #BFDBFE" : "3px solid transparent",
                transition: "border-color 0.2s ease",
            }}
            role="group"
            aria-labelledby={labelId}
        >
            <Typography component="label" id={labelId} sx={{ fontSize: "16px", fontWeight: 600, color: "#111827", fontFamily: FONT_FAMILY, lineHeight: 1.4, display: "block", textAlign: "left" }}>
                {question.label}
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: "0px" }}>
                <SliderSelector value={value} onChange={onChange} labelId={labelId} />
                <Box sx={{ display: "flex", justifyContent: "space-between", padding: "0 10px", marginTop: "2px" }}>
                    <Typography aria-hidden="true" sx={{ fontSize: "14px", color: COLORS.textMuted, fontFamily: FONT_FAMILY }}>
                        {lowLabel ?? "Rarely"}
                    </Typography>
                    <Typography aria-hidden="true" sx={{ fontSize: "14px", color: COLORS.textMuted, fontFamily: FONT_FAMILY }}>
                        {highLabel ?? "Often"}
                    </Typography>
                </Box>
            </Box>
        </Box>
    )
}


function SaveTreeDialog({ open, onClose, onSave, saving, error }) {
    const [title, setTitle] = useState("")

    useEffect(() => {
        if (open) setTitle("")
    }, [open])

    const handleSaveClick = () => {
        const trimmed = title.trim()
        if (!trimmed || saving) return
        onSave(trimmed)
    }

    return (
        <Dialog
            open={open}
            onClose={saving ? undefined : onClose}
            fullWidth
            maxWidth="xs"
            PaperProps={{ sx: { borderRadius: "16px", backgroundColor: "#FDFBFA", padding: "28px" } }}
        >
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 3 }}>
                <Typography
                    component="h2"
                    sx={{ fontFamily: FONT_HEADING, fontWeight: 400, fontSize: "2rem", color: COLORS.primary, lineHeight: 1.2 }}
                >
                    Give your tree a name
                </Typography>
                <IconButton onClick={onClose} disabled={saving} aria-label="Close" sx={{ mt: "-4px", mr: "-8px" }}>
                    <svg width="18" height="18" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                        <path d="M1 1L15 15M15 1L1 15" stroke="#374151" strokeWidth="1.75" strokeLinecap="round" />
                    </svg>
                </IconButton>
            </Box>

            <TextField
                label="Tree Title*"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") handleSaveClick() }}
                fullWidth
                autoFocus
                disabled={saving}
                placeholder="Class Period 1"
                sx={{
                    mb: 3,
                    "& .MuiOutlinedInput-root": { borderRadius: "8px", fontFamily: FONT_FAMILY, fontSize: "20px" },
                }}
            />

            {error && (
                <Typography sx={{ fontSize: 12, color: COLORS.error, fontFamily: FONT_FAMILY, mb: 2 }} role="alert">
                    {error}
                </Typography>
            )}

            <Button
                onClick={handleSaveClick}
                disabled={!title.trim() || saving}
                variant="contained"
                fullWidth
                sx={{
                    py: 1.5,
                    fontSize: 16,
                    fontWeight: 600,
                    fontFamily: FONT_FAMILY,
                    textTransform: "none",
                    borderRadius: "6px",
                    backgroundColor: COLORS.primaryDark,
                    boxShadow: "none",
                    "&:hover": BUTTON_HOVER_SX,
                    "&.Mui-disabled": { backgroundColor: COLORS.disabled, color: "#fff" },
                }}
            >
                {saving ? "Saving…" : "Save Tree to Library"}
            </Button>
        </Dialog>
    )
}


function inferAnswersFromTree(tree) {
    if (!tree) return null
    if (tree.answers) return tree.answers
    if (tree.totalLeaves == null || !Array.isArray(tree.slotMap)) return null

    const closestIndex = (value, options) => {
        let bestIdx = 0
        let bestDiff = Infinity
        options.forEach((opt, idx) => {
            const diff = Math.abs(opt - value)
            if (diff < bestDiff) {
                bestDiff = diff
                bestIdx = idx
            }
        })
        return bestIdx + 1
    }

    const flowerCounts = { making_claim: 0, providing_evidence: 0, relating: 0 }
    tree.slotMap.forEach((type) => {
        if (type && flowerCounts[type] !== undefined) flowerCounts[type] += 1
    })

    return {
        overallTalk: closestIndex(tree.totalLeaves, LEAF_COUNTS),
        making_claim: closestIndex(flowerCounts.making_claim, FLOWER_COUNTS.making_claim),
        providing_evidence: closestIndex(flowerCounts.providing_evidence, FLOWER_COUNTS.providing_evidence),
        relating: closestIndex(flowerCounts.relating, FLOWER_COUNTS.relating),
    }
}

function BuildTreeStep({ isMobile, onTreeSaved, showSuccessToast }) {
    const [initialSavedTree] = useState(() => (SET_SIMULATOR_TO_LAST_TREE ? getSimulatedTrees()[0] ?? null : null))
    const [initialAnswers] = useState(() => inferAnswersFromTree(initialSavedTree) ?? INITIAL_ANSWERS)

    const [answers, setAnswers] = useState(initialAnswers)
    const [hasInteracted, setHasInteracted] = useState(() => !!initialSavedTree)
    const slotMapRef = useRef(initialSavedTree?.slotMap ?? null)
    const prevAnswersRef = useRef(initialAnswers)

    const [saveDialogOpen, setSaveDialogOpen] = useState(false)
    const [helpOpen, setHelpOpen] = useState(false)
    const [isSaving, setIsSaving] = useState(false)
    const [saveError, setSaveError] = useState(null)
    const [saveConfirmation, setSaveConfirmation] = useState(false)

    const [showPickedUpNote, setShowPickedUpNote] = useState(() => !!initialSavedTree)

    const setAnswer = (key, val) => {
        if (!hasInteracted) setHasInteracted(true)
        setShowPickedUpNote(false)
        setAnswers((prev) => ({ ...prev, [key]: val }))
    }

    const anyAnswered = Object.values(answers).some((v) => v !== null)
    const allAnswered = answers.overallTalk !== null && answers.making_claim !== null && answers.providing_evidence !== null && answers.relating !== null
    const answeredCount = Object.values(answers).filter((v) => v !== null).length

    const treeInputs = useMemo(() => {
        if (!anyAnswered) return null
        const counts = computeFlowerCounts(answers)
        const totalLeaves = answers.overallTalk == null ? 0 : LEAF_COUNTS[answers.overallTalk - 1]
        if (slotMapRef.current === null) {
            slotMapRef.current = buildInitialSlotMap(counts)
            prevAnswersRef.current = { ...answers }
            return { totalLeaves, slotMap: slotMapRef.current }
        }
        const prev = prevAnswersRef.current
        for (const key of TALK_MOVE_KEYS) {
            if (prev[key] !== answers[key]) slotMapRef.current = updateSlotMapForType(slotMapRef.current, key, counts[key])
        }
        prevAnswersRef.current = { ...answers }
        return { totalLeaves, slotMap: slotMapRef.current }
    }, [answers, anyAnswered])

    const handleSaveTreeClick = () => {
        mixpanel.track("treeSimulatorSaveClicked")
        setSaveError(null)
        setSaveDialogOpen(true)
    }

    const handleConfirmSave = async (title) => {
        // Saved locally as a cookie rather than to a backend, so it can be
        // listed alongside real session trees without a server round trip.
        setIsSaving(true)
        setSaveError(null)
        try {
            const saved = saveSimulatedTree({
                title,
                totalLeaves: treeInputs ? treeInputs.totalLeaves : 0,
                slotMap: treeInputs ? treeInputs.slotMap : new Array(MAX_FLOWERS).fill(null),
                answers,
            })
            mixpanel.track("treeSimulatorTreeSaved", { answers })
            mixpanel.people.increment('simulatedTreesSaved')
            setSaveDialogOpen(false)
            setSaveConfirmation(true)
            setTimeout(() => setSaveConfirmation(false), 2500)
            if (showSuccessToast) {
                showSuccessToast({
                    type: 'success',
                    title: 'Tree saved to your library.',
                    message: `"${title}" is ready in the Talk Trees Library.`,
                })
            }
            if (onTreeSaved) onTreeSaved(saved.id)
        } catch (e) {
            console.error("Failed to save tree:", e)
            setSaveError("Something went wrong saving your tree. Please try again.")
        } finally {
            setIsSaving(false)
        }
    }

    const cardStyle = {
        border: `1px solid ${COLORS.hairline}`,
        borderRadius: "8px",
        backgroundColor: "#FDFBFA",
        padding: isMobile ? "16px" : "20px 24px",
    }

    return (
            <div style={{ fontFamily: FONT_FAMILY, display: "flex", flexDirection: "column", paddingBottom: isMobile ? "16px" : 0, width: "100%" }}>
            
            <Box sx={{
                mt: '10px', mb: 2, textAlign: "left",
                display: "flex",
                flexDirection: isMobile ? "column" : "row",
                justifyContent: "space-between",
                alignItems: isMobile ? "flex-start" : "center",
                gap: isMobile ? 1.5 : 2,
            }}>
                <Box>
                    <Typography
                        component="h1"
                        sx={{ fontFamily: FONT_HEADING, fontWeight: 400, fontSize: '1.25rem', color: COLORS.text, lineHeight: 1.2, mb: 0.75 }}
                    >
                        Tree Simulator
                    </Typography>
                    <Typography sx={{ fontSize: 16, color: COLORS.textMuted, fontFamily: FONT_FAMILY, lineHeight: 1.5 }}>
                        Answer 4 questions about your classroom to preview what your Talk Tree might look like.
                    </Typography>
                </Box>

                <Box
                    onClick={() => setHelpOpen(true)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setHelpOpen(true) } }}
                    sx={{ display: "flex", alignItems: "center", gap: "6px", cursor: "pointer", flexShrink: 0 }}
                >
                    <Typography sx={{ fontSize: "14px", color: COLORS.primary, fontWeight: 500, fontFamily: FONT_FAMILY }}>
                        Help me use the simulator to create a tree
                    </Typography>
                    <Box sx={{ width: 18, height: 18, borderRadius: "50%", backgroundColor: COLORS.primary, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: "11px", fontWeight: 700, flexShrink: 0 }}>
                        i
                    </Box>
                </Box>
            </Box>

            {showPickedUpNote && initialSavedTree && (
                <Typography sx={{ fontSize: "13px", color: COLORS.textMuted, fontFamily: FONT_FAMILY, mb: 2, textAlign: "left" }}>
                    "{initialSavedTree.title}" selections are shown below. Change any answer to explore a new tree.
                </Typography>
            )}

            <div
                style={{
                    display: "grid",
                    gridTemplateColumns: isMobile ? "1fr" : "5fr 7fr",
                    gap: isMobile ? "16px" : "24px",
                    alignItems: "stretch",
                    background: "#FDFBFA",
                    padding: isMobile ? "12px" : "16px",
                    borderRadius: "8px",
                    boxSizing: "border-box",
                }}
            >
                <div style={{ display: "flex", flexDirection: "column", gap: isMobile ? "24px" : "20px", padding: isMobile ? "8px" : "5px" }} role="group" aria-label="Talk tree questions">
                    {ALL_QUESTIONS.map((q) => (
                        <QuestionBlock key={q.key} question={q} value={answers[q.key]} onChange={(val) => setAnswer(q.key, val)} showHighlight={hasInteracted} lowLabel={q.lowLabel} highLabel={q.highLabel} />
                    ))}
                </div>

                <div style={{ ...cardStyle, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
                    {anyAnswered && (
                        <Typography sx={{ margin: "0 0 8px 0", fontSize: "16px", fontWeight: 700, color: "#111827", fontFamily: FONT_FAMILY, alignSelf: "center" }}>Simulated Talk Tree</Typography>
                    )}

                    <div style={{ width: "100%", maxWidth: isMobile ? 320 : 440, boxSizing: "border-box", display: "flex", flexDirection: "column", alignItems: "center", padding: "8px 8px 4px" }}>
                        {!anyAnswered && (
                            <Typography sx={{ fontSize: "13px", fontWeight: 600, color: "#374151", fontFamily: FONT_FAMILY, margin: "8px 0 4px", textAlign: "center" }} role="status" aria-live="polite">
                                Move any slider to start growing your tree
                            </Typography>
                        )}

                        <div style={{ width: "100%" }} aria-hidden={!anyAnswered}>
                            <SimulatedTalkTreePreview
                                totalLeaves={treeInputs ? treeInputs.totalLeaves : 0}
                                slotMap={treeInputs ? treeInputs.slotMap : new Array(MAX_FLOWERS).fill(null)}
                                skeleton={!anyAnswered}
                                showLegend={true}
                            />
                        </div>

                        {anyAnswered && (
                            <Typography sx={{ fontSize: "11.5px", color: COLORS.textMuted, fontFamily: FONT_FAMILY, textAlign: "center", lineHeight: 1.5, margin: "8px 0 0", maxWidth: 380 }}>
                                Simulated preview based on your answers, not an analysis of a real classroom recording.
                            </Typography>
                        )}
                    </div>
                    {allAnswered && (
                        <Box sx={{ mt: 2, width: "100%", maxWidth: 440, display: "flex", flexDirection: "column", alignItems: "center" }}>
                            <Button
                                onClick={handleSaveTreeClick}
                                variant="contained"
                                fullWidth
                                aria-label="Save tree to library"
                                sx={{
                                    py: 1.3,
                                    fontSize: 15,
                                    fontWeight: 600,
                                    fontFamily: FONT_FAMILY,
                                    textTransform: "none",
                                    borderRadius: "4px",
                                    backgroundColor: COLORS.primaryDark,
                                    boxShadow: "none",
                                    "&:hover": BUTTON_HOVER_SX,
                                    "&:focus-visible": { outline: `3px solid ${COLORS.primary}`, outlineOffset: "2px" },
                                }}
                            >
                                Save Tree
                            </Button>
                            {saveConfirmation && (
                                <Typography sx={{ fontSize: 13, color: COLORS.primary, fontFamily: FONT_FAMILY, textAlign: "center", mt: 1 }} role="status" aria-live="polite">
                                    Tree saved to your library.
                                </Typography>
                            )}
                        </Box>
                    )}
                </div>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "5fr 7fr", gap: isMobile ? "16px" : "24px", marginTop: "16px", padding: isMobile ? "0 12px" : "0 16px", boxSizing: "border-box" }}>
                <div style={{ gridColumn: isMobile ? "auto" : "2", display: "flex", flexDirection: "column", alignItems: "center" }}>
                    {anyAnswered && !allAnswered && (
                        <div style={{ display: "flex", alignItems: "center", gap: "8px" }} role="group" aria-label={`${answeredCount} of 4 questions answered`}>
                            <div style={{ display: "flex", gap: "6px" }}>
                                {ALL_QUESTIONS.map((q) => (
                                    <div
                                        key={q.key}
                                        aria-hidden="true"
                                        style={{ width: "10px", height: "10px", borderRadius: "50%", backgroundColor: answers[q.key] !== null ? COLORS.primary : "#d1d5db", transition: "background-color 0.2s ease" }}
                                    />
                                ))}
                            </div>
                            <span style={{ fontSize: "12px", color: COLORS.textMuted, fontFamily: FONT_FAMILY }}>{`${4 - answeredCount} more to complete your tree`}</span>
                        </div>
                    )}
                </div>
            </div>

            <SaveTreeDialog
                open={saveDialogOpen}
                onClose={() => { if (!isSaving) setSaveDialogOpen(false) }}
                onSave={handleConfirmSave}
                saving={isSaving}
                error={saveError}
            />

            <SimulatorHelpDialog open={helpOpen} onClose={() => setHelpOpen(false)} />
        </div>
    )
}


export default function TreeSimulator({ onTreeSaved, showSuccessToast }) {
    const theme = useTheme()
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"))
    const muiTheme = createTheme({
        typography: {
            fontFamily: FONT_FAMILY,
            h4: { fontFamily: FONT_HEADING },
            h5: { fontFamily: FONT_HEADING },
            h6: { fontFamily: FONT_HEADING },
        },
    })

    return (
        <ThemeProvider theme={muiTheme}>
            <Box component="main" aria-label="Tree Simulator" sx={{ fontFamily: FONT_FAMILY }}>
                <BuildTreeStep isMobile={isMobile} onTreeSaved={onTreeSaved} showSuccessToast={showSuccessToast} />
            </Box>
        </ThemeProvider>
    )
}